import { ref, readonly, computed, onScopeDispose, type Ref, type ComputedRef } from 'vue';
import type { GameSnapshot, ClientMessage } from '@/types/generated';
import type { ServerMessageMap } from './webSocketSingleton';
import type { AppGameState } from './useGameFlow';
import type { GameSession } from './useGameSession';
import STRINGS from '@/constants/strings';
import { getMetrics } from '@/telemetry/metrics';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

export interface UseGameSnapshotOptions {
  timerState: {
    startTimer: (serverStartTime: number, duration: number, onExpiry?: () => void) => void;
    stopTimer: () => void;
    isActive: Readonly<Ref<boolean>>;
  };
  gameId: Readonly<Ref<string>>;
  gameState: Readonly<Ref<AppGameState>>;
  wordSubmitted: Readonly<Ref<boolean>>;
  opponentSubmitted: Readonly<Ref<boolean>>;
  handleSnapshot: (snapshot: GameSnapshot) => void;
  handleError: (error: string) => void;
  gameSession: { save: (session: GameSession) => void; clear: () => void };
  playerNameStore: { save: (name: string) => void };
  sendMessage: (msg: ClientMessage) => boolean;
  onMessage: <T extends keyof ServerMessageMap>(
    messageType: T,
    handler: (data: ServerMessageMap[T]) => void
  ) => () => void;
}

export interface UseGameSnapshotReturn {
  currentWord: Ref<string>;
  selectionTimerDuration: Readonly<Ref<number>>;
  submissionType: Readonly<Ref<'manual' | 'auto-word' | 'auto-empty'>>;
  canSubmitWord: ComputedRef<boolean>;
  submitButtonText: ComputedRef<string>;
  currentPlayerStatus: ComputedRef<string>;
  opponentStatus: ComputedRef<string>;
  handleWordSubmit: () => void;
  resetTimerPhaseState: () => void;
}

export function useGameSnapshot(options: UseGameSnapshotOptions): UseGameSnapshotReturn {
  const {
    timerState,
    gameId,
    gameState,
    wordSubmitted,
    opponentSubmitted,
    handleSnapshot,
    handleError,
    gameSession,
    playerNameStore,
    sendMessage,
    onMessage,
  } = options;

  const selectionTimerDuration = ref(30000);
  const currentWord = ref<string>('');
  const submissionType = ref<'manual' | 'auto-empty' | 'auto-word'>('manual');

  let expiryGraceTimeout: ReturnType<typeof setTimeout> | null = null;
  let inProgressStart: number | null = null;
  // Tracks the server start_time of the currently-running selection timer.
  // StateRestored must be idempotent: receiving the same SelectingLetters +
  // active-timer snapshot must not restart the timer or re-arm the grace
  // timeout, or a server stuck in SelectingLetters drives an infinite
  // StateRefreshRequest loop (issue #104).
  let selectionTimerStartTime: number | null = null;

  const clearExpiryGraceTimeout = () => {
    if (expiryGraceTimeout !== null) {
      clearTimeout(expiryGraceTimeout);
      expiryGraceTimeout = null;
    }
  };

  const startGraceTimeout = (ms: number) => {
    clearExpiryGraceTimeout();
    expiryGraceTimeout = setTimeout(() => {
      expiryGraceTimeout = null;
      if (gameId.value) {
        const sent = sendMessage({ StateRefreshRequest: { game_id: gameId.value } });
        if (!sent) {
          // useConnectionErrorHandling watches connectionStatus and shows the overlay
          // when the WebSocket closes, so by the time sendMessage returns false the
          // user already has a visible error. Log for diagnostics only.
          reportClientError(
            new Error(`Grace timeout: StateRefreshRequest failed to send (gameId=${gameId.value})`),
            ErrorLocation.GameSnapshotGraceTimeout
          );
        }
      } else {
        reportClientError(
          new Error('Grace timeout fired but gameId is null'),
          ErrorLocation.GameSnapshotGraceTimeout
        );
      }
    }, ms);
  };

  const resetTimerPhaseState = () => {
    clearExpiryGraceTimeout();
    currentWord.value = '';
    submissionType.value = 'manual';
    inProgressStart = null;
    selectionTimerStartTime = null;
  };

  const canSubmitWord = computed(() => {
    return timerState.isActive.value && currentWord.value.trim().length > 0 && !wordSubmitted.value;
  });

  const submitButtonText = computed(() => {
    if (wordSubmitted.value) {
      return submissionType.value === 'manual'
        ? STRINGS.ui.buttons.submitted
        : STRINGS.ui.buttons.autoSubmitted;
    }
    if (!timerState.isActive.value) return STRINGS.ui.buttons.timeUp;
    return STRINGS.ui.buttons.submit;
  });

  const currentPlayerStatus = computed(() => {
    if (wordSubmitted.value) {
      if (submissionType.value === 'auto-word') return STRINGS.status.player.autoSubmittedWord;
      if (submissionType.value === 'auto-empty') return STRINGS.status.player.autoSubmittedEmpty;
      return STRINGS.status.player.submitted;
    }
    if (currentWord.value.trim().length > 0) return STRINGS.status.player.typing;
    if (timerState.isActive.value) return STRINGS.status.player.thinking;
    return STRINGS.status.player.ready;
  });

  const opponentStatus = computed(() => {
    if (opponentSubmitted.value) return STRINGS.status.player.submitted;
    return STRINGS.status.player.thinking;
  });

  /**
   * Attempts to submit a word.
   * Returns without action if already submitted (server-authoritative state).
   */
  const trySubmitWord = (word: string, kind: 'manual' | 'auto-word' | 'auto-empty') => {
    if (wordSubmitted.value) {
      return;
    }
    submissionType.value = kind;

    if (!gameId.value || typeof gameId.value !== 'string') {
      reportClientError(
        new Error(`Invalid game ID for word submission: kind=${kind}`),
        ErrorLocation.GameSnapshotWordSubmitInvalidId
      );
      handleError('Invalid game state');
      return;
    }

    const success = sendMessage({
      SubmitWord: {
        game_id: gameId.value,
        word,
        submission_time: Date.now(),
      },
    });

    if (success) {
      // Record word submission duration (time from in-progress start to submit)
      if (inProgressStart && kind === 'manual') {
        getMetrics().uiWordSubmissionDurationMs.record(Date.now() - inProgressStart);
      }
    } else {
      if (kind !== 'manual') {
        getMetrics().gameAutoSubmitFailures.add(1);
      }
      reportClientError(
        new Error(`Failed to submit word: kind=${kind}`),
        ErrorLocation.GameSnapshotWordSubmitFailed
      );
      submissionType.value = 'manual';
      handleError(STRINGS.errors.connection.failedToSubmit);
    }
  };

  /**
   * Auto-submission handler - called when timer expires
   */
  const handleAutoSubmission = () => {
    const word = currentWord.value?.trim() || '';
    trySubmitWord(word, word.length > 0 ? 'auto-word' : 'auto-empty');
  };

  /**
   * Manual word submission triggered by user action
   */
  const handleWordSubmit = () => {
    const word = currentWord.value?.trim();
    if (!word || !timerState.isActive.value) return;
    trySubmitWord(word, 'manual');
  };

  const unsubscribeFunctions: (() => void)[] = [];

  // Unified GameSnapshot handler
  unsubscribeFunctions.push(
    onMessage('GameSnapshot', (snapshot: GameSnapshot) => {
      const { event, state } = snapshot;

      // Handle timer-related side effects before delegating to useGameFlow
      switch (event) {
        case 'LetterSelected':
          // Start selection timer if present and in selection phase
          if (
            state.timer &&
            state.timer.is_active &&
            state.status === 'SelectingLetters' &&
            !timerState.isActive.value
          ) {
            selectionTimerDuration.value = state.timer.duration;
            selectionTimerStartTime = state.timer.start_time;
            timerState.startTimer(state.timer.start_time, state.timer.duration, () => {
              // Server auto-fills remaining letters on selection timer expiry.
              // Grace period: if no SelectionComplete/TimerStarted arrives within
              // 200ms, request a state refresh (the notification may have been lost).
              startGraceTimeout(200);
            });
          }
          break;

        // A bulk auto-selection (bot / "pick for me" / timer auto-fill) is
        // always a completed selection, so it shares SelectionComplete's timer
        // side-effects: cancel the expiry grace and stop the selection timer.
        case 'SelectionComplete':
        case 'LettersBulkSelected':
          clearExpiryGraceTimeout();
          selectionTimerStartTime = null;
          // Stop selection timer (word-finding timer starts on TimerStarted)
          timerState.stopTimer();
          break;

        case 'TimerStarted':
          if (state.timer && state.timer.is_active) {
            inProgressStart = Date.now();
            selectionTimerStartTime = null;
            timerState.startTimer(state.timer.start_time, state.timer.duration, () => {
              handleAutoSubmission();
              // Start grace period: if no TimerEnded/RoundCompleted arrives
              // within 3s, request a state refresh from the server
              startGraceTimeout(3000);
            });
            // Only reset word UI if we haven't already transitioned to in-progress
            // (SelectionComplete arrives first and the user may have started typing)
            if (gameState.value !== 'in-progress') {
              currentWord.value = '';
              submissionType.value = 'manual';
            }
          } else {
            reportClientError(
              new Error('TimerStarted received but timer is missing or inactive'),
              ErrorLocation.GameSnapshotTimerStarted
            );
            startGraceTimeout(200);
          }
          break;

        case 'TimerEnded':
          clearExpiryGraceTimeout();
          if (gameState.value === 'in-progress') {
            handleAutoSubmission();
          }
          timerState.stopTimer();
          break;

        case 'StateRestored':
          // Must precede the timer restore below. Finalizing a game rewrites only
          // the game meta, so a game the reaper reaped mid-round comes back with
          // its round timer still Active — leaving the local timer to run out,
          // fire handleAutoSubmission, and have the rejected SubmitWord replace
          // game-over with the error screen.
          if (state.status === 'Finalized') {
            clearExpiryGraceTimeout();
            timerState.stopTimer();
            break;
          }
          // Restore timer if active
          if (state.timer && state.timer.is_active) {
            if (state.status === 'InProgress') {
              selectionTimerStartTime = null;
              timerState.startTimer(
                state.timer.start_time,
                state.timer.duration,
                handleAutoSubmission
              );
            } else if (state.status === 'SelectingLetters') {
              // Idempotency guard: if we've already started a selection timer
              // with this same server start_time, do not restart it or re-arm
              // the grace timeout. Without this, a server stuck in
              // SelectingLetters echoes StateRestored on every refresh and the
              // client spins in a ~200ms StateRefreshRequest loop (issue #104).
              //
              // Key on start_time alone — do NOT conjunct with
              // `timerState.isActive.value`. Once the local timer expires
              // (server start_time > duration in the past), `isActive` flips
              // false, and a same-start_time echo would fall through, restart
              // the timer, fire the grace, and send StateRefreshRequest.
              if (selectionTimerStartTime === state.timer.start_time) {
                break;
              }
              selectionTimerDuration.value = state.timer.duration;
              selectionTimerStartTime = state.timer.start_time;
              timerState.startTimer(state.timer.start_time, state.timer.duration, () => {
                // Same grace-period logic as the LetterSelected handler
                startGraceTimeout(200);
              });
            } else {
              reportClientError(
                new Error(`StateRestored: active timer in unexpected status: ${state.status}`),
                ErrorLocation.GameSnapshotStateRestored
              );
            }
          } else if (state.status === 'InProgress') {
            console.debug('[useGameSnapshot] StateRestored: timer already expired on reconnect', {
              status: state.status,
            });
            if (!state.player_submitted) {
              handleAutoSubmission();
            }
          }
          break;

        case 'NewRoundRequested':
          break;

        case 'RoundCompleted':
          clearExpiryGraceTimeout();
          break;

        case 'GameFinalized':
        case 'OpponentEndedGame':
        case 'ServerError':
          clearExpiryGraceTimeout();
          gameSession.clear();
          break;

        case 'GameStarted': {
          // Save session for reconnection
          gameSession.save({
            game_id: state.game_id,
            player_id: state.player_id,
          });
          // Persist player name separately (survives game session clears)
          const player = state.players.find(p => p.id === state.player_id);
          if (player) {
            playerNameStore.save(player.name);
          } else {
            reportClientError(
              new Error('GameStarted: player_id not found in players list'),
              ErrorLocation.GameSnapshotPlayerNotFound
            );
          }
          break;
        }
      }

      // Delegate to useGameFlow for state management
      // Note: no messages arrive before onMounted completes, so handleSnapshot
      // is always available when this handler fires.
      handleSnapshot(snapshot);

      // Post-snapshot: start selection timer for NewRoundStarting
      // (must happen after handleSnapshot which resets game state and stops timer)
      if (event === 'NewRoundStarting' && state.timer && state.timer.is_active) {
        selectionTimerDuration.value = state.timer.duration;
        selectionTimerStartTime = state.timer.start_time;
        timerState.startTimer(state.timer.start_time, state.timer.duration);
      }
    })
  );

  unsubscribeFunctions.push(
    onMessage('SubmissionResult', data => {
      if (!data.accepted) {
        getMetrics().gameActionRejected.add(1);
        submissionType.value = 'manual';
        if (!data.reason) {
          reportClientError(
            new Error('SubmissionResult rejected with no reason from server'),
            ErrorLocation.GameSnapshotSubmissionRejectedNoReason
          );
        }
      }
    })
  );

  // The reaper finalized an idle game under the player. It carries only the game_id,
  // so ask for a snapshot rather than guessing at a screen: the reply is a
  // StateRestored carrying the real Finalized status and round results, which
  // useGameFlow already maps to game-over.
  unsubscribeFunctions.push(
    onMessage('GameFinalizedInactivity', data => {
      // A notification for a game the player has already left is expected — they
      // moved on and the reaper caught up later. Nothing to resync.
      if (!gameId.value || data.game_id !== gameId.value) return;
      const sent = sendMessage({ StateRefreshRequest: { game_id: gameId.value } });
      if (!sent) {
        // The socket is down; the reconnect will deliver a snapshot. Log for
        // diagnostics only.
        reportClientError(
          new Error(
            `GameFinalizedInactivity: StateRefreshRequest failed to send (gameId=${gameId.value})`
          ),
          ErrorLocation.GameSnapshotInactivityRefreshFailed
        );
      }
    })
  );

  onScopeDispose(() => {
    unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    clearExpiryGraceTimeout();
  });

  return {
    currentWord,
    selectionTimerDuration: readonly(selectionTimerDuration),
    submissionType: readonly(submissionType),
    canSubmitWord,
    submitButtonText,
    currentPlayerStatus,
    opponentStatus,
    handleWordSubmit,
    resetTimerPhaseState,
  };
}
